import { useState } from "react";
import { Icon } from "../icons";
import { FormLabel, ScrollInput } from "./shared";
import { apiFetch } from "../api";
import { EXERCISE_CATALOGUE, SET_DEFAULTS, DIFFICULTY, DIFF_COLOR, DIFF_BG } from "../constants";
import { uid, today, calcOrm } from "../helpers";


const blankEntry = () => ({ id: uid(), sets: "", reps: "", weight: "", difficulty: "Medium", setsHint: SET_DEFAULTS.sets, repsHint: SET_DEFAULTS.reps, weightHint: SET_DEFAULTS.weight });

// ── Single set entry row ───────────────────────────────────────────────────────
function EntryRow({ entry, idx, onChange, onDelete, units = "lbs" }) {
  const displayReps   = entry.reps   || entry.repsHint;
  const displayWeight = entry.weight || entry.weightHint;
  const est = calcOrm(displayWeight, displayReps);

  return (
    <div style={{
      background:"var(--bg3)", borderRadius:10, padding:"12px 12px 10px",
      marginBottom:8, border:"1px solid var(--border)",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
        <span style={{ fontSize:11, color:"var(--dim)", fontWeight:700 }}>{idx + 1}</span>
        <div style={{ flex:1 }}/>
        {est && <span style={{ fontSize:11, color:"var(--accent)", fontWeight:700 }}>~{est} 1RM</span>}
        <button onClick={onDelete} style={{ background:"none", border:"none", cursor:"pointer",
          color:"var(--dim)", fontSize:18, padding:"2px 4px", lineHeight:1 }}>✕</button>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr auto 1fr auto 1fr", gap:6, alignItems:"center", marginBottom:10 }}>
        <div>
          <div style={{ fontSize:9, color:"var(--dim)", textAlign:"center", marginBottom:4,
            textTransform:"uppercase", letterSpacing:"0.08em" }}>Sets</div>
          <ScrollInput
            value={entry.sets}
            hint={entry.setsHint || "3"}
            step={1}
            min={1}
            onChange={v => onChange({ ...entry, sets: v })}
          />
        </div>

        <span style={{ color:"var(--dim)", fontSize:14, paddingTop:16, textAlign:"center" }}>×</span>

        <div>
          <div style={{ fontSize:9, color:"var(--dim)", textAlign:"center", marginBottom:4,
            textTransform:"uppercase", letterSpacing:"0.08em" }}>Reps</div>
          <ScrollInput
            value={entry.reps}
            hint={entry.repsHint || SET_DEFAULTS.reps}
            step={1}
            min={1}
            onChange={v => onChange({ ...entry, reps: v })}
          />
        </div>

        <span style={{ color:"var(--dim)", fontSize:14, paddingTop:16, textAlign:"center" }}>@</span>

        <div>
          <div style={{ fontSize:9, color:"var(--dim)", textAlign:"center", marginBottom:4,
            textTransform:"uppercase", letterSpacing:"0.08em" }}>lbs</div>
          <ScrollInput
            value={entry.weight}
            hint={entry.weightHint || SET_DEFAULTS.weight}
            step={units === "kg" ? 2.5 : 5}
            min={0}
            onChange={v => onChange({ ...entry, weight: v })}
          />
        </div>
      </div>

      <div style={{ display:"flex", gap:6 }}>
        {DIFFICULTY.map(d => (
          <button key={d} onClick={() => onChange({ ...entry, difficulty: d })} style={{
            flex:1, padding:"7px 4px", borderRadius:7, fontSize:11, fontWeight:700,
            fontFamily:"inherit", cursor:"pointer",
            border:`1px solid ${entry.difficulty === d ? DIFF_COLOR[d] : "var(--border)"}`,
            background: entry.difficulty === d ? DIFF_BG[d] : "transparent",
            color: entry.difficulty === d ? DIFF_COLOR[d] : "var(--dim)",
          }}>{d}</button>
        ))}
      </div>
    </div>
  );
}

// ── Main modal ─────────────────────────────────────────────────────────────────
function hintsFromExercise(ex) {
  if (!ex?.sets?.length) return null;
  const lastDate = ex.sets.reduce((max, s) => (s.date > max ? s.date : max), ex.sets[0].date);
  const last = ex.sets.filter(s => s.date === lastDate);
  // Group identical reps/weight/difficulty
  const order = [], map = new Map();
  for (const s of last) {
    const key = `${s.reps}|${s.weight}|${s.difficulty}`;
    if (map.has(key)) { map.get(key).count++; }
    else { const g = { count: 1, reps: s.reps, weight: s.weight, difficulty: s.difficulty || "Medium" }; map.set(key, g); order.push(g); }
  }
  return order.map(g => ({
    id: uid(), sets: "", reps: "", weight: "",
    setsHint: String(g.count),
    repsHint: g.reps   ? String(g.reps)   : SET_DEFAULTS.reps,
    weightHint: g.weight ? String(g.weight) : SET_DEFAULTS.weight,
    difficulty: g.difficulty,
  }));
}

export default function ExerciseModal({ existing, allExercises = [], onClose, onSave, units = "lbs" }) {
  const [name, setName]           = useState(existing?.name || "");
  const [muscles, setMuscles]     = useState(
    existing?.muscles?.length ? existing.muscles : [existing?.muscle].filter(Boolean)
  );
  const [oneRepMax, setOneRepMax] = useState(existing?.oneRepMax || "");
  const [category, setCategory]   = useState(existing?.category || "");
  const [entries, setEntries]     = useState(() => {
    const hints = existing ? hintsFromExercise(existing) : null;
    return hints || [blankEntry()];
  });
  const [notes, setNotes]         = useState(existing?.notes || "");
  const [touched, setTouched]     = useState({});
  const [dupeError, setDupeError] = useState("");
  const [suggestions, setSuggestions]         = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const nameErr = touched.name && !name.trim();

  const handleNameChange = async (val) => {
    setName(val);
    setDupeError("");
    if (val.length >= 2) {
      const q = val.toLowerCase();
      const local = EXERCISE_CATALOGUE.filter(e => e.name.toLowerCase().includes(q)).slice(0, 6);
      const names = new Set(local.map(e => e.name.toLowerCase()));
      // Include user's own exercises not already in catalogue
      const mine = allExercises
        .filter(e => e.name.toLowerCase().includes(q) && !names.has(e.name.toLowerCase()))
        .map(e => ({ name: e.name, muscles: e.muscles, category: e.category, source: "mine" }));
      mine.forEach(e => names.add(e.name.toLowerCase()));
      let server = [];
      try { server = await apiFetch(`/exercises/search?q=${encodeURIComponent(val)}`); } catch {}
      const merged = [
        ...local.map(e => ({ ...e, source: "catalogue" })),
        ...mine,
        ...server.filter(e => !names.has(e.name.toLowerCase())).map(e => ({ ...e, source: "user" })),
      ];
      setSuggestions(merged);
      setShowSuggestions(merged.length > 0);
    } else {
      setSuggestions([]); setShowSuggestions(false);
    }
  };

  const applySuggestion = s => {
    setName(s.name);
    if (s.muscles?.length) setMuscles(s.muscles);
    else if (s.muscle) setMuscles([s.muscle]);
    if (s.category) setCategory(s.category);
    setShowSuggestions(false);
    // If this exercise exists in user history, pre-populate hints from last session
    const userEx = allExercises.find(e => e.name.toLowerCase() === s.name.toLowerCase());
    const hints = userEx ? hintsFromExercise(userEx) : null;
    if (hints) setEntries(hints);
  };

  const addEntry    = () => setEntries(p => {
    const last = p[p.length - 1];
    return [...p, last
      ? { id: uid(), sets: "", reps: "", weight: "",
          setsHint: last.sets || last.setsHint || "3",
          repsHint: last.reps || last.repsHint || SET_DEFAULTS.reps,
          weightHint: last.weight || last.weightHint || SET_DEFAULTS.weight,
          difficulty: last.difficulty }
      : blankEntry()
    ];
  });
  const removeEntry = id => setEntries(p => p.filter(e => e.id !== id));
  const updateEntry = (id, upd) => setEntries(p => p.map(e => e.id === id ? upd : e));

  const handleSave = () => {
    setTouched({ name: true });
    if (!name.trim()) return;
    // Block duplicate exercise names (only for new exercises)
    if (!existing) {
      const dupe = allExercises.find(e => e.name.toLowerCase() === name.trim().toLowerCase());
      if (dupe) {
        setDupeError(`"${dupe.name}" already exists in your exercises — find it in the Exercises tab to edit it.`);
        return;
      }
    }
    setDupeError("");
    const primaryMuscle = muscles[0] || "Full Body";
    // Only log sets the user explicitly typed (reps or weight non-empty)
    const expandedSets = entries
      .filter(entry => entry.reps !== "" || entry.weight !== "")
      .flatMap(entry => {
        const setsVal   = parseInt(entry.sets)   || parseInt(entry.setsHint)   || 1;
        const repsVal   = parseInt(entry.reps)   || parseInt(entry.repsHint)   || 0;
        const weightVal = parseFloat(entry.weight) || parseFloat(entry.weightHint) || 0;
        const count = Math.max(1, setsVal);
        return Array.from({ length: count }, () => ({
          id: uid(),
          sets: 1,
          reps: repsVal,
          weight: weightVal,
          difficulty: entry.difficulty || "Medium",
          date: today(),
        }));
      });
    onSave({
      id: existing?.id || uid(),
      name: name.trim(),
      muscle: primaryMuscle,
      muscles,
      category: category,
      oneRepMax: parseFloat(oneRepMax) || null,
      sets: expandedSets,
      notes,
      updatedAt: today(),
    });
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-md">
        <div className="modal-header">
          <div className="modal-title">{existing ? "Edit Exercise" : "New Exercise"}</div>
          <button className="modal-close" onClick={onClose}><Icon.X /></button>
        </div>

        <div className="modal-body">

          {/* Name + autocomplete */}
          <div className="form-group" style={{ position:"relative" }}>
            <FormLabel required>Exercise Name</FormLabel>
            <input
              className={`input ${nameErr ? "error" : ""}`}
              placeholder="e.g. Bench Press"
              value={name}
              onChange={e => handleNameChange(e.target.value)}
              onBlur={() => { setTouched(p => ({ ...p, name: true })); setTimeout(() => setShowSuggestions(false), 160); }}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            />
            {nameErr && <div className="field-error">Name is required</div>}
            {dupeError && <div className="field-error">{dupeError}</div>}
            {showSuggestions && (
              <div style={{
                position:"absolute", top:"100%", left:0, right:0, zIndex:200,
                background:"var(--bg2)", border:"1px solid var(--border2)",
                borderRadius:8, marginTop:4, overflow:"hidden",
                boxShadow:"0 8px 24px rgba(0,0,0,0.4)",
              }}>
                {suggestions.map((s, i) => (
                  <div key={i} onMouseDown={() => applySuggestion(s)} style={{
                    padding:"10px 14px", cursor:"pointer",
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                    borderBottom:"1px solid var(--border)",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--bg3)"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}
                  >
                    <span style={{ fontSize:13, fontWeight:500 }}>{s.name}</span>
                    {s.category && <span className={`badge badge-${s.category === "powerlifting" ? "accent" : s.category === "weightlifting" ? "teal" : "slate"}`}>{s.category}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Muscle groups — read-only, set from catalogue */}
          {muscles.length > 0 && (
            <div className="form-group">
              <FormLabel>Muscle Groups</FormLabel>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {muscles.map((m, i) => (
                  <span key={m} style={{
                    padding:"4px 10px", borderRadius:20, fontSize:12, fontWeight: i===0 ? 700 : 500,
                    background: i===0 ? "var(--accent)" : "var(--bg3)",
                    color: i===0 ? "#111" : "var(--text)",
                    border: `1px solid ${i===0 ? "var(--accent)" : "var(--border2)"}`,
                  }}>
                    {i===0 && <span style={{ fontSize:10, marginRight:3 }}>★</span>}{m}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 1RM */}
          <div className="form-group">
            <FormLabel>1 Rep Max (lbs)</FormLabel>
            <input className="input" type="text" inputMode="decimal"
              placeholder="e.g. 225" value={oneRepMax}
              onChange={e => setOneRepMax(e.target.value)}
              style={{ maxWidth:160 }}/>
          </div>

          {/* Set entries — only for new exercises; use Log Session to record sets */}
          {!existing && (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <FormLabel>Sets</FormLabel>
                <span/>
              </div>
              {entries.map((entry, i) => (
                <EntryRow key={entry.id} entry={entry} idx={i} units={units}
                  onChange={upd => updateEntry(entry.id, upd)}
                  onDelete={() => removeEntry(entry.id)} />
              ))}
              <button onClick={addEntry} style={{
                width:"100%", padding:"11px", borderRadius:8, marginTop:2,
                border:"1px dashed var(--border2)", background:"transparent",
                color:"var(--muted)", fontSize:13, cursor:"pointer", fontFamily:"inherit",
              }}>+ Add Entry</button>
            </div>
          )}

          {/* Notes */}
          <div className="form-group">
            <FormLabel>Notes</FormLabel>
            <textarea className="textarea" placeholder="Form cues, observations…"
              value={notes} onChange={e => setNotes(e.target.value)} style={{ minHeight:56 }}/>
          </div>

        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}>
            {existing ? "Save Changes" : "Add Exercise"}
          </button>
        </div>
      </div>
    </div>
  );
}
